import React, { useState } from 'react';
import { Modal, Typography, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from 'react-apollo';

import UPDATE_LINE_ITEM_DSP_SETTINGS from '../../../../core/GraphQl/Mutations/UPDATE_LINE_ITEM_DSP_SETTINGS';
import UPDATE_CAMPAIGN_ORDER from '../../../../core/GraphQl/Mutations/UPDATE_CAMPAIGN_ORDER';
import CREATE_CAMPAIGN_HISTORY from '../../../../core/GraphQl/Mutations/CREATE_CAMPAIGN_HISTORY';
import CURRENT_USER from '../../../../core/GraphQl/Queries/CURRENT_USER';
import sanitizePayload from '../../../../core/utils/sanitizePayload';

const { Text } = Typography;

// Line items showing a "Ready for Update" badge — the pending statuses that
// only the programmatic launch flow (backend) would normally clear.
const PENDING_LINE_ITEM_STATUSES = ['PLAY_PENDING', 'PAUSED_PENDING'];
// Order statuses that indicate a submitted-but-not-pushed update.
const PENDING_ORDER_STATUSES = ['LIVE_PENDING', 'LIVE_APPROVED', 'PAUSED_PENDING', 'PAUSED_APPROVED'];

const ORDER_STATUS_TRANSITIONS = {
  LIVE_PENDING: 'LIVE',
  LIVE_APPROVED: 'LIVE',
  PAUSED_PENDING: 'PAUSED',
  PAUSED_APPROVED: 'PAUSED',
};

/**
 * "Mark Update as Applied at DSP" utility.
 *
 * For launched campaign orders whose updates were applied MANUALLY at the DSP
 * (because the programmatic update flow is not used), this clears the stuck
 * "Ready for Update" indicators:
 *   1. Line items: PLAY_PENDING -> PLAY, PAUSED_PENDING -> PAUSE
 *   2. Campaign order: LIVE_PENDING/LIVE_APPROVED -> LIVE, PAUSED_PENDING/PAUSED_APPROVED -> PAUSED
 *   3. Writes a campaign history entry documenting the manual completion.
 *
 * @param {Object} params
 * @param {Object} params.campaignOrder - The current campaign order (with campaigns + line items)
 * @param {Function} [params.refetchCampaignOrder] - Refetch callback after success
 */
const useMarkUpdateApplied = ({ campaignOrder, refetchCampaignOrder } = {}) => {
  const [isMarkingApplied, setIsMarkingApplied] = useState(false);

  const [updateLineItemDspSettings] = useMutation(UPDATE_LINE_ITEM_DSP_SETTINGS);
  const [updateCampaignOrder] = useMutation(UPDATE_CAMPAIGN_ORDER);
  const [createCampaignHistory] = useMutation(CREATE_CAMPAIGN_HISTORY);
  const { data: currentUserData } = useQuery(CURRENT_USER);

  const pendingLineItems = (campaignOrder?.campaigns || []).flatMap((campaign) =>
    (campaign.lineItems || []).filter(
      (lineItem) => lineItem.dspLineItemId && PENDING_LINE_ITEM_STATUSES.includes(lineItem.status)
    )
  );

  const isOrderStatusPending = PENDING_ORDER_STATUSES.includes(campaignOrder?.status);
  const hasPendingState = pendingLineItems.length > 0 || isOrderStatusPending;

  const markUpdateApplied = () => {
    if (!campaignOrder?.id) return;

    const newOrderStatus = ORDER_STATUS_TRANSITIONS[campaignOrder.status];

    Modal.confirm({
      title: 'Mark update as applied at DSP',
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      content: (
        <div>
          <p>
            Use this only after the campaign changes have <Text strong>actually been applied manually at the DSP</Text>.
            This clears the pending update indicators without pushing through the programmatic flow.
          </p>
          {pendingLineItems.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary">Line items to mark as applied:</Text>
              <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                {pendingLineItems.map((lineItem) => (
                  <li key={lineItem.id}>
                    {lineItem.name}: {lineItem.status === 'PLAY_PENDING' ? 'PLAY_PENDING → PLAY' : 'PAUSED_PENDING → PAUSE'}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {newOrderStatus && (
            <p style={{ marginBottom: 0 }}>
              <Text type="secondary">
                Campaign order status: {campaignOrder.status} → {newOrderStatus}
              </Text>
            </p>
          )}
        </div>
      ),
      okText: 'Mark as Applied',
      okButtonProps: { danger: false },
      cancelText: 'Cancel',
      onOk: async () => {
        setIsMarkingApplied(true);
        const appliedLineItems = [];

        try {
          // 1. Clear pending line-item statuses
          for (const lineItem of pendingLineItems) {
            try {
              await updateLineItemDspSettings({
                variables: sanitizePayload({
                  lineItemId: lineItem.id,
                  data: { status: lineItem.status === 'PLAY_PENDING' ? 'PLAY' : 'PAUSE' },
                }),
              });
              appliedLineItems.push(lineItem);
            } catch (error) {
              // Stop before flipping the order status: partially-cleared state is
              // recoverable (re-running this utility finishes the rest).
              message.error(
                `Failed to mark "${lineItem.name}" as applied: ${error.message}. Order status was not changed — fix the issue and run again.`
              );
              return;
            }
          }

          // 2. Clear pending campaign-order status
          if (newOrderStatus) {
            await updateCampaignOrder({
              variables: sanitizePayload({
                where: { id: campaignOrder.id },
                data: { status: newOrderStatus },
              }),
            });
          }

          // 3. Audit trail documenting the manual completion
          try {
            await createCampaignHistory({
              variables: {
                action: 'UPDATED',
                property: 'BASICS',
                data: {
                  property: 'status',
                  oldValue: campaignOrder.status,
                  newValue: newOrderStatus || campaignOrder.status,
                  note: 'Update applied manually at DSP; pending update indicators cleared.',
                  lineItems: appliedLineItems.map((li) => ({
                    id: li.id,
                    name: li.name,
                    status: li.status === 'PLAY_PENDING' ? 'PLAY' : 'PAUSE',
                  })),
                },
                authorId: currentUserData?.currentUser?.id,
                campaignOrderId: campaignOrder.id,
              },
            });
          } catch (error) {
            // Non-fatal: state is already reconciled, only the audit entry failed
            console.warn('Failed to write campaign history entry for manual update completion:', error);
          }

          message.success('Update marked as applied at DSP.');
        } catch (error) {
          console.error('Error marking update as applied:', error);
          message.error(`Failed to mark update as applied: ${error.message}`);
        } finally {
          setIsMarkingApplied(false);
          if (refetchCampaignOrder) {
            try {
              await refetchCampaignOrder();
            } catch (error) {
              console.warn('Failed to refetch campaign order:', error);
            }
          }
        }
      },
    });
  };

  return { markUpdateApplied, isMarkingApplied, hasPendingState, pendingLineItems };
};

export default useMarkUpdateApplied;
